import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { LucideProps } from '@vitness/icons';
import { Search, CornerDownLeft } from '@vitness/icons';
import { vp } from '@vitness/tokens';
import { useAccent } from '@vitness/theme';

/**
 * A single actionable entry in the {@link CommandPalette}. Apps supply these so
 * the universal search can jump to products, screens, or run quick actions.
 */
export interface CommandItem {
  id: string;
  label: string;
  /** Group heading the item is bucketed under (e.g. `Products`, `Actions`). */
  group?: string;
  icon?: ComponentType<LucideProps>;
  /** Extra terms matched against the query in addition to the label. */
  keywords?: string[];
  /** Optional trailing hint (e.g. `Open console →`). */
  hint?: string;
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  placeholder?: string;
  commands?: CommandItem[];
  /** Shown when the query matches nothing. */
  emptyHint?: ReactNode;
}

function matches(item: CommandItem, query: string): boolean {
  if (!query) return true;
  const haystack = [item.label, item.group ?? '', ...(item.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .every((term) => haystack.includes(term));
}

/**
 * Universal search overlay — the ⌘K command palette mirrored from the
 * system-design reference (`Platform.dc.html`). Renders a centred modal with a
 * search input and grouped, keyboard-navigable results. Fully inline-styled so
 * it renders correctly in apps without Tailwind (e.g. the platform host). The
 * search corpus is supplied by the host via `commands`; this component owns
 * only the filtering, selection, and keyboard interaction.
 */
export function CommandPalette({
  open,
  onClose,
  placeholder = 'Search products, docs, endpoints…',
  commands = [],
  emptyHint = 'No matches. Try a different term.',
}: CommandPaletteProps) {
  const { safe } = useAccent();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const filtered = useMemo(
    () => commands.filter((command) => matches(command, query)),
    [commands, query],
  );

  // Reset query/selection and focus the input each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  // Keep the highlight within bounds as the result set shrinks.
  useEffect(() => {
    setActive((current) => (current >= filtered.length ? 0 : current));
  }, [filtered.length]);

  // Lock page scroll while open so nothing scrolls behind the overlay. Pad for
  // the removed scrollbar width to avoid a layout shift on lock/unlock.
  useEffect(() => {
    if (!open) return undefined;
    const { body } = document;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, [open]);

  if (!open) return null;

  const select = (item: CommandItem | undefined) => {
    if (!item) return;
    item.onSelect();
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(filtered[active]);
    }
  };

  // Preserve group order as first-seen in the command list.
  const groups: string[] = [];
  for (const item of filtered) {
    const key = item.group ?? '';
    if (!groups.includes(key)) groups.push(key);
  }

  let flatIndex = -1;

  return createPortal(
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: vp('13vh'),
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
        style={{
          width: 560,
          maxWidth: vp('92vw'),
          background: 'var(--vn-surface-card)',
          border: '1px solid var(--vn-border-strong)',
          borderRadius: 'var(--vn-radius-xl)',
          boxShadow: 'var(--vn-shadow-modal)',
          overflow: 'hidden',
        }}
      >
        {/* Search field */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '14px 16px',
            borderBottom: '1px solid var(--vn-border-default)',
          }}
        >
          <Search width={17} height={17} stroke="var(--vn-text-muted)" strokeWidth={1.7} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--vn-text-primary)',
              fontFamily: 'var(--vn-font-body)',
              fontSize: 14,
            }}
          />
          <kbd
            style={{
              fontFamily: 'var(--vn-font-mono)',
              fontSize: 9,
              border: '1px solid var(--vn-border-strong)',
              borderRadius: 'var(--vn-radius-xs)',
              padding: '2px 6px',
              color: 'var(--vn-text-secondary)',
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div style={{ padding: 8, maxHeight: vp('50vh'), overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div
              style={{
                padding: '22px 12px',
                textAlign: 'center',
                fontSize: 12.5,
                color: 'var(--vn-text-muted)',
              }}
            >
              {emptyHint}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group || '_'}>
                {group && (
                  <div
                    style={{
                      fontFamily: 'var(--vn-font-mono)',
                      fontSize: 8.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: 'var(--vn-text-muted)',
                      padding: '8px 10px 5px',
                    }}
                  >
                    {group}
                  </div>
                )}
                {filtered
                  .filter((item) => (item.group ?? '') === group)
                  .map((item) => {
                    flatIndex += 1;
                    const index = flatIndex;
                    const isActive = index === active;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => select(item)}
                        onMouseMove={() => setActive(index)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 11,
                          padding: '9px 10px',
                          borderRadius: 'var(--vn-radius-md)',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          background: isActive ? 'var(--vn-surface-card-hover)' : 'transparent',
                          color: 'var(--vn-text-primary)',
                        }}
                      >
                        {Icon && (
                          <Icon
                            width={16}
                            height={16}
                            stroke={isActive ? safe : 'var(--vn-text-secondary)'}
                            strokeWidth={1.8}
                          />
                        )}
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{item.label}</span>
                        {item.hint && (
                          <span
                            style={{
                              fontFamily: 'var(--vn-font-mono)',
                              fontSize: 9,
                              color: 'var(--vn-text-muted)',
                            }}
                          >
                            {item.hint}
                          </span>
                        )}
                        {isActive && !item.hint && (
                          <CornerDownLeft
                            width={13}
                            height={13}
                            stroke="var(--vn-text-muted)"
                            strokeWidth={1.8}
                          />
                        )}
                      </button>
                    );
                  })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default CommandPalette;
